import { Pencil, Gamepad2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useLocale } from "@/hooks/useLocale";

interface EditorPlaySwitcherProps {
  worldId: string;
  adventureId: string;
  /** Which view the user is currently in. */
  current: "editor" | "play";
  /** Compact variant hides text labels on very small screens. */
  compact?: boolean;
  className?: string;
}

/**
 * Segmented two-state toggle that lets a world owner jump between the
 * world Editor (in adventure-overlay mode) and the live Play view of the
 * same adventure. Always renders both options and highlights the active one.
 */
export function EditorPlaySwitcher({
  worldId,
  adventureId,
  current,
  compact = false,
  className,
}: EditorPlaySwitcherProps) {
  const navigate = useNavigate();
  const { t } = useLocale();

  const goEditor = () => {
    if (current === "editor") return;
    navigate(`/world/${worldId}?adventure=${adventureId}`);
  };
  const goPlay = () => {
    if (current === "play") return;
    navigate(`/play/${adventureId}`);
  };

  const baseBtn =
    "inline-flex items-center gap-1.5 h-7 px-2 sm:px-2.5 text-[11px] sm:text-xs font-medium rounded-md transition-colors";
  const active = "bg-background text-foreground shadow-sm";
  const inactive = "text-muted-foreground hover:text-foreground";

  return (
    <div
      role="tablist"
      aria-label={t.play_mode_switch_aria}
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/60 p-0.5 shrink-0",
        className,
      )}
    >
      <button
        type="button"
        role="tab"
        aria-selected={current === "editor"}
        onClick={goEditor}
        className={cn(baseBtn, current === "editor" ? active : inactive)}
      >
        <Pencil className="w-3 h-3" />
        <span>{t.play_mode_editor}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={current === "play"}
        onClick={goPlay}
        className={cn(baseBtn, current === "play" ? active : inactive)}
      >
        <Gamepad2 className="w-3 h-3" />
        <span>{t.play_mode_play}</span>
      </button>
    </div>
  );
}